import React from 'react';
import type { StrategyTrade, TradeEvent } from '@/services/strategyService';

/**
 * Full per-trade audit — WHY it entered (the scanner signal it fired on), the
 * event timeline, and WHY it exited. Reused by the strategies page (paper + live
 * automated trades) and the scenario/backtest trade viewer so the audit looks the
 * same everywhere. Pure presentation; renders whatever the backend captured.
 */

// Humanize a signal-snapshot key into a short label. Key-agnostic to camel/Pascal
// casing because backend dictionary keys are not name-policy transformed.
const labelFor = (key: string): string => {
  const k = key.toLowerCase();
  if (k === 'rsrating') return 'RS';
  if (k === 'price') return 'Price';
  if (k === 'volume') return 'Vol';
  if (k === 'atr') return 'ATR';
  if (k === 'atrfromvwap') return 'VWAP (ATR)';
  if (k === 'relativevolume') return 'Rel Vol';
  if (k === 'changepercent') return 'Chg';
  if (k === 'dailyrsi2') return 'RSI2';
  if (k === 'vwap') return 'VWAP';
  if (k.includes('52whigh')) return 'vs 52w High';
  if (k.includes('52wlow')) return 'vs 52w Low';
  const m = k.match(/sma(\d+)/);
  if (m && k.includes('dist')) return `vs SMA${m[1]}`;
  // Fallback: insert spaces before capitals, strip a trailing "Pct".
  return key.replace(/Pct$/, '').replace(/([A-Z])/g, ' $1').trim();
};

const isPct = (key: string) => /pct|percent/i.test(key);

const fmtValue = (key: string, v: number): string => {
  const k = key.toLowerCase();
  if (isPct(key)) return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
  if (k === 'relativevolume') return `${v.toFixed(2)}×`;
  if (k === 'rsrating') return String(Math.round(v));
  if (k === 'atrfromvwap') return `${v >= 0 ? '+' : ''}${v.toFixed(2)} ATR`;
  if (k === 'price' || k === 'vwap') return `$${v.toFixed(2)}`;
  if (k === 'volume') {
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return String(v);
  }
  return v.toFixed(2);
};

// Colour cue: green when supportive of a long-trend signal, red when not.
const toneFor = (key: string, v: number): string => {
  const k = key.toLowerCase();
  if (k === 'rsrating') return v >= 70 ? 'text-green-600 dark:text-green-400' : v > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground';
  if (isPct(key) || k === 'changepercent' || k === 'atrfromvwap')
    return v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  return 'text-foreground';
};

// Stable, readable ordering: identity first, then trend, then context.
const ORDER = ['price', 'rsrating', 'distfromsma50pct', 'distfromsma150pct', 'distfromsma200pct',
  'distfrom52whighpct', 'distfrom52wlowpct', 'relativevolume', 'changepercent', 'dailyrsi2', 'atr', 'atrfromvwap', 'volume'];
const sortKeys = (keys: string[]) =>
  [...keys].sort((a, b) => {
    const ia = ORDER.indexOf(a.toLowerCase()); const ib = ORDER.indexOf(b.toLowerCase());
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

export const SignalChips: React.FC<{ signal: Record<string, number> }> = ({ signal }) => {
  const keys = sortKeys(Object.keys(signal));
  if (keys.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {keys.map((k) => (
        <span key={k}
          className="inline-flex items-baseline gap-1 rounded-md border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[11px]">
          <span className="text-muted-foreground">{labelFor(k)}</span>
          <span className={`font-medium tabular-nums ${toneFor(k, signal[k])}`}>{fmtValue(k, signal[k])}</span>
        </span>
      ))}
    </div>
  );
};

const evTime = (iso: string) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const TradeAuditDetail: React.FC<{ trade: StrategyTrade }> = ({ trade }) => {
  const entryEvent = trade.events?.find((e) => e.type === 'Entry');
  const entrySignal = entryEvent?.signal ?? null;

  return (
    <div className="space-y-3 text-xs">
      {/* WHY it entered */}
      <section>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
          Entry signal
        </div>
        <div className="text-foreground break-words">{trade.entryReason || '—'}</div>
        {entrySignal && Object.keys(entrySignal).length > 0 && (
          <div className="mt-1.5"><SignalChips signal={entrySignal} /></div>
        )}
      </section>

      {/* Event timeline */}
      {trade.events && trade.events.length > 0 && (
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            Events
          </div>
          <div className="space-y-1.5">
            {trade.events.map((e: TradeEvent, i) => (
              <div key={i} className="rounded-md border border-border/50 bg-background/40 p-2">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span className="font-medium">{e.type}</span>
                  <span className="text-muted-foreground tabular-nums">{evTime(e.time)}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-muted-foreground tabular-nums">
                  <span>@ ${e.price?.toFixed(2)}</span>
                  {e.stopPrice != null && <span>stop ${e.stopPrice.toFixed(2)}</span>}
                </div>
                {e.notes && <div className="mt-0.5 text-muted-foreground break-words">{e.notes}</div>}
                {e.signal && Object.keys(e.signal).length > 0 && (
                  <div className="mt-1.5"><SignalChips signal={e.signal} /></div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WHY it exited */}
      {trade.status === 'Closed' && (
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            Exit
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-muted-foreground tabular-nums">
            <span>Reason: <span className="text-foreground font-medium">{trade.exitReason || '—'}</span></span>
            {trade.exitPrice != null && <span>@ ${trade.exitPrice.toFixed(2)}</span>}
            <span className={trade.realizedPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
              {trade.realizedPnL >= 0 ? '+' : ''}${trade.realizedPnL.toFixed(2)} ({trade.realizedRMultiple.toFixed(1)}R)
            </span>
          </div>
        </section>
      )}
    </div>
  );
};

export default TradeAuditDetail;
